import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import classNames from 'classnames';

import { deriveMapStateFromConfig, TWC } from '@tmapy/beetlejuice-utils';
import { useDispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';
import {
  BtnBar,
  CtaBtn,
  CustomSelect,
  DangerAlert,
  Dialog,
  Form,
  FormItem,
  FormRow,
  Checkbox,
  InfoAlert,
  Input,
  MultiSelect,
  SecondaryBtn,
  Select,
  Textarea,
  WaitingAlert,
  WarningAlert,
  CloseDialogBtn,
  SuccessAlert,
} from '@tmapy/style-guide';

import type { LogResourceType } from '../../types';
import type {
  BrnoDocRetrieveResponse,
  DocRequest,
  NestedGeodesistResponse,
  PatchedDocRequest,
  UserListResponse,
} from '../../openapi/types';
import { FlowEnum } from '../../openapi/types/FlowEnum';
import { PrejimkaTypesEnum } from '../../openapi/types/PrejimkaTypesEnum';
import { UtilitiesTypeEnum } from '../../openapi/types/UtilitiesTypeEnum';

import { cadastersList } from '../../openapi/operations/cadastersList';
import { geodesistsList } from '../../openapi/operations/geodesistsList';
import { usersList } from '../../openapi/operations/usersList';
import { DocsRetrieveResponse } from '../../openapi/operations/docsRetrieve';
import { docsCreate } from '../../openapi/operations/docsCreate';
import { docsDestroy } from '../../openapi/operations/docsDestroy';
import { docsListOperation } from '../../openapi/operations/docsList';
import { docsImportInterruptPartialUpdate } from '../../openapi/operations/docsImportInterruptPartialUpdate';

import { brnoDocsClosePartialUpdate } from '../../openapi/operations/brnoDocsClosePartialUpdate';
import { brnoDocsPartialUpdate } from '../../openapi/operations/brnoDocsPartialUpdate';
import { brnoDocsSignPartialUpdate } from '../../openapi/operations/brnoDocsSignPartialUpdate';
import { brnoDocsRejectPartialUpdate } from '../../openapi/operations/brnoDocsRejectPartialUpdate';
import { brnoDocsCloseInterruptPartialUpdate } from '../../openapi/operations/brnoDocsCloseInterruptPartialUpdate';
import { brnoDocsCancelPartialUpdate } from '../../openapi/operations/brnoDocsCancelPartialUpdate';
import { brnoDocsSubmitPartialUpdate } from '../../openapi/operations/brnoDocsSubmitPartialUpdate';
import { useBrnoDocsRetrieveResource } from '../../openapi/operations/brnoDocsRetrieve';

import { LocationWhisperer } from '../../app/components/LocationWhisperer';
import { MainContent } from '../../app/components/MainContent';
import { mapConfig } from '../../prejimka/mapConfig';
import { MapWithAreas } from '../../areas/components/MapWithAreas';
import { MapWrapper } from '../../app/components/MapWrapper';
import { PageTitle } from '../../app/components/PageTitle';
import { ResWhisperer } from '../../app/components/ResWhisperer';
import { LogList } from '../../logs/components/LogList';
import { HistoryList } from '../../history/components/HistoryList';

import type { InputProps } from '../../hooks/useInput';
import {
  SSE_TYPE,
  useAppContext,
  useAuth,
  useLogDownloadLink,
  useFormItemServerErrors,
  useHistoryAction,
  useInput,
  useMultiSelect,
  useSelect,
  useSelector,
  useSSE,
  useAction,
  toBtnQueryState,
} from '../../hooks';
import { useCheckbox } from '../../hooks/useCheckbox';
import { QUERY_STATE } from '../../constants';
import { actionResourceInvalidate } from '../../redux/resource/actions';

import {
  DOC_STATE,
  DOCDETAIL_LOAD_SUCCESS,
  DOCDETAIL_LOAD_WAITING,
  EDITOR_VISIBLE_STATE,
  REJECTION_REASON_STATES,
  usableItemsForFormType,
} from '../constants';
import { updateDoc, uploadSignedProtocol } from '../actions/docDetail';
import { getFormValues } from '../utils/getformvalues';

import {
  docFormMessages,
  docTitleMessages,
  docUtilitiesTypeMessages,
  rejectionMessages,
  rejectionReasonMessages,
} from './docsMessages';
import { docEditButtonsMsg, docEditMessages, validatorMessages } from './docEditMessages';

import { defaultValues } from './defaultValues';
import { DocAttachments } from './DocAttachments';
import { DocBadge } from './DocBadge';
import { CheckRecordList } from './CheckRecordList';
import { Rejection } from './Rejection';
import { UploadBtn } from './UploadBtn';
import { DocDownloadFiles } from './DocDownloadFiles';

import './Doc.scss';

const renderCadasterMultiSelectItem = (item: { name: string }) => item.name;
const renderUserSelectItem = (item: UserListResponse | NestedGeodesistResponse) => (
  <>
    {item.name} {item.surname}
  </>
);

export type DocBrnoEditContainerProps = {
  docId?: number;
};

export const DocBrno: React.FC<DocBrnoEditContainerProps> = ({ docId }) => {
  useSSE(SSE_TYPE.DOCS, docId);

  const formatMessage = useMessage();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { appType } = useAppContext();

  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [deleteQueryState, setDeleteQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [checkCancelState, setCancelQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [checkSubmitState, setSubmitQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [closeQueryState, setCloseQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [closeInterruptQueryState, setCloseInterruptQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [importInterruptQueryState, setImportInterruptQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [signQueryState, setSignQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);

  const [validate, setValidate] = useState(false);
  const [isRejectionDialogShown, showRejectionDialog] = useState(false);
  const [isLogDialogShown, showLogDialog] = useState(false);
  const [checkLogId, setCheckLogId] = useState<number | undefined>(undefined);

  const userUrl = useSelector((state) => state.auth.url);
  const removeQueryState = useSelector((state) => state.doc.removeQueryState);
  const uploadProtocolQueryState = useSelector((state) => state.doc.uploadProtocolQueryState);
  const serverErrors = useSelector((state) => state.docedit.errors);
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const auth = useAuth();

  const stateData = useSelector((state) => state.doc.data);
  const resource = useBrnoDocsRetrieveResource(docId ? { id: docId } : null);

  const mapState = useMemo(() => deriveMapStateFromConfig({ map: mapConfig() } as TWC), []);
  const mapExtent = useMemo(() => {
    return resource.data?.rozsah_aktualizacni_zmeny_extent.length
      ? resource.data.rozsah_aktualizacni_zmeny_extent
      : undefined;
  }, [resource.data?.rozsah_aktualizacni_zmeny_extent]);

  const logResourceType: LogResourceType = 'doc';

  const data: BrnoDocRetrieveResponse = useMemo(() => {
    const cachedData = stateData.id === docId ? stateData : resource.data;
    return docId && cachedData
      ? (cachedData as BrnoDocRetrieveResponse)
      : ({
          ...defaultValues,
          state: DOC_STATE.INPROGRESS,
          prejimka_type: PrejimkaTypesEnum.GP,
          flow: FlowEnum.GP,
        } as BrnoDocRetrieveResponse);
  }, [resource.data, stateData, docId]);

  const usableItems = usableItemsForFormType['doc'];

  const acl = useMemo(
    () => ({
      canUpdate: auth.hasPermission('BrnoDocs', 'partial_update', data),

      docsUpdate: auth.hasPermission('BrnoDocs', 'update', data),
      docsDestroy: auth.hasPermission('BrnoDocs', 'destroy', data),
      docsActionClose: auth.hasPermission('BrnoDocs', 'close', data),
      docsActionReject: auth.hasPermission('BrnoDocs', 'reject', data),
      docsActionAccept: auth.hasPermission('BrnoDocs', 'accept', data),
      docsActionSubmit: auth.hasPermission('BrnoDocs', 'submit', data),
      docsActionCancel: auth.hasPermission('BrnoDocs', 'cancel', data),
      docsActionMapReview: auth.hasPermission('BrnoDocs', 'action.map_review', data),
      docsActionShowLog: auth.hasPermission('BrnoDocs', 'logs', data),
      docsActionChecksVisibility: auth.hasPermission('BrnoDocs', 'checks_visibility', data),
      canUploadSignedProtocol: auth.hasPermission('BrnoDocs', 'upload_signed_protocol', data),
      docsUpdateAuthor: auth.hasPermission('BrnoDocs', 'update.author', data),
      docsUpdateEditorManager: auth.hasPermission('BrnoDocs', 'update.editor_manager', data),
      docsActionImportInterrupt: auth.hasPermission('BrnoDocs', 'dtm_import_interrupt', data),
      docsActionCloseInterrupt: auth.hasPermission('BrnoDocs', 'dtm_close_interrupt', data),

      //download section
      docsActionDownloadProtocol: auth.hasPermission(
        'BrnoDocs',
        'download_handover_protocol',
        data,
      ),
      docsActionDownloadCloseLog: auth.hasPermission('BrnoDocs', 'download_close_log', data),
      docsActionDownloadImportLog: auth.hasPermission('BrnoDocs', 'download_import_log', data),
    }),
    [auth, data],
  );
  const routeList = useHistoryAction('docList', { appType });
  const routeDetail = useHistoryAction('docDetail', { appType, id: docId! });

  const [showLogDownloadError, setShowLogDownloadError] = useState(false);
  const handleShowDownloadLogError = (visible: boolean) => {
    setShowLogDownloadError(visible);
  };

  const closeLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_close_log',
    handleShowDownloadLogError,
  );

  const importLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_import_log',
    handleShowDownloadLogError,
  );

  const handoverProtocolDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_handover_protocol',
    handleShowDownloadLogError,
  );

  const showLogs = usableItems?.includes('logs') && acl.docsActionShowLog;
  const showChecks = usableItems?.includes('checks') && acl.docsActionChecksVisibility;

  useEffect(() => {
    if (!docId) {
      setSaveQueryState(QUERY_STATE.EMPTY);
    }
    if (!resource.data) {
      dispatch({
        type: DOCDETAIL_LOAD_WAITING,
      });
    } else {
      dispatch({
        type: DOCDETAIL_LOAD_SUCCESS,
        data: resource.data,
      });
    }
  }, [docId, resource.data, dispatch, setSaveQueryState]);

  const handleRefetch = useCallback(() => {
    resource.refetch();
  }, [resource]);

  const actionQuery = useMemo(() => ({ id: docId }), [docId]);
  const { run: doAction, isActionRunning } = useAction({
    query: actionQuery,
    onRefetch: resource.refetch,
    routeList,
    listPath: docsListOperation().path,
  });

  const handleSaveDocWithValidation = async () => {
    setValidate(true);

    const isSomeFormItemInvalid = Object.values(formItems).some((formItem: any) => {
      return formItem.validator?.(formItem.value);
    });

    if (!isSomeFormItemInvalid) {
      return await handleSaveDocWithoutValidation();
    }
    return null;
  };

  const handleSaveDocWithoutValidation = async () => {
    const formValues = getFormValues(formItems) as any;
    const values = {
      ...formValues,
      prejimka_type: data.prejimka_type,
      // multiselect
      cadasters: formValues.cadasters.map((cadaster: any) => cadaster.url),
      utilities_type: formValues.utilities_type,
      // select
      geodesist: formValues.geodesist.url || '',
      // author
      author: formValues.author?.url || userUrl,
    };

    return await handleSave(values);
  };

  const handleSave = useCallback(
    async (values: DocRequest | PatchedDocRequest) => {
      setSaveQueryState(QUERY_STATE.WAITING);
      let response;

      try {
        if (docId) {
          response = await brnoDocsPartialUpdate({
            query: { id: docId },
            request: values,
          });

          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);
            handleRefetch();
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        } else {
          response = await docsCreate({
            request: values as DocRequest,
          });
          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);

            routeDetail.replace({ appType, id: response.data.id });
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        }
      } finally {
        const docsList = docsListOperation();
        dispatch(actionResourceInvalidate(docsList.path));
      }
      if (response.ok) {
        const attachments = response.data.attachments ?? [];
        return {
          download_attachments_link: '',
          ...response.data,
          attachments,
        };
      }
      return null;
    },
    [docId, routeDetail, appType, dispatch],
  );

  const handleDelete = useCallback(async () => {
    try {
      setDeleteQueryState(QUERY_STATE.WAITING);
      const response = await docsDestroy({ query: { id: docId! } });
      if (response.ok) {
        setDeleteQueryState(QUERY_STATE.SUCCESS);

        routeList();
      } else {
        setDeleteQueryState(QUERY_STATE.FAIL);
      }
      return response;
    } catch (e) {
      setDeleteQueryState(QUERY_STATE.FAIL);
    } finally {
      const docsList = docsListOperation();
      dispatch(actionResourceInvalidate(docsList.path));
    }
    return null;
  }, [routeList, setDeleteQueryState, dispatch, docId]);

  const handleRejectBtnClick = () => {
    showRejectionDialog(true);
  };
  const handleRejectCancelBtnClick = () => {
    showRejectionDialog(false);
  };
  const handleCloseInterruptBtnClick = () => {
    doAction(brnoDocsCloseInterruptPartialUpdate, setCloseInterruptQueryState);
  };
  const handleImportInterruptBtnClick = () => {
    doAction(docsImportInterruptPartialUpdate, setImportInterruptQueryState);
  };
  const handleCloseBtnClick = () => {
    doAction(brnoDocsClosePartialUpdate, setCloseQueryState);
  };
  const handleCancelBtnClick = () => {
    doAction(brnoDocsCancelPartialUpdate, setCancelQueryState);
  };
  const handleSubmitBtnClick = () => {
    doAction(brnoDocsSubmitPartialUpdate, setSubmitQueryState);
  };
  const handleAcceptBtnClick = () => {
    doAction(brnoDocsSignPartialUpdate, setSignQueryState);
  };

  const handleProtocolUpload = useCallback(
    (e: React.ChangeEvent) => {
      const file = (e as any).dataTransfer || (e as any).target.files[0];
      if (!file) return;

      if (
        file.name.indexOf(`${data.id}`) > -1 ||
        window.confirm(formatMessage(docEditButtonsMsg.docUploadProtocolFileNameError))
      ) {
        dispatch(uploadSignedProtocol(file, data.url, data.id));
      }
    },
    [dispatch, data.id, data.url, formatMessage],
  );

  const handleCheckClick = useCallback((id: number) => {
    setCheckLogId(id);
    showLogDialog(true);
  }, []);

  // HOOK FOR INPUTS
  const isEditable =
    !docId || (!data.original_id && acl.canUpdate) || (data.original_id && data.editable);

  const hasPlanimetryRef = useRef(!!data.has_planimetry);
  const hasUtilitiesRef = useRef(!!data.has_utilities);

  useEffect(() => {
    hasPlanimetryRef.current = !!data.has_planimetry;
    hasUtilitiesRef.current = !!data.has_utilities;
  }, [data.has_planimetry, data.has_utilities]);

  const formItemChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
    setValidate(false);
  }, [setValidate, setSaveQueryState]);

  // TODO refactor component AddressAutocomplete -> hooks
  const processorAutocompleteRef = useRef({});
  const handleProcessorAutocompleteChange = (
    e: React.ChangeEvent,
    data: { ident: string; value: string }[],
  ) => {
    processorAutocompleteRef.current = data;
  };

  const ownerAutocompleteRef = useRef({});
  const handleOwnerAutocompleteChange = (
    e: React.ChangeEvent,
    data: { ident: string; value: string }[],
  ) => {
    ownerAutocompleteRef.current = data;
  };

  const utilitiesTypeItems = useSelectMessages(docUtilitiesTypeMessages, UtilitiesTypeEnum);

  const utilities_type = useInput({
    name: 'utilities_type',
    isDisabled: !isEditable,
    initialValue: data.utilities_type || UtilitiesTypeEnum.ALL_UTILITIES,
    items: utilitiesTypeItems,
    label: formatMessage(docFormMessages.utilities_type),
    onChange: formItemChangeCallback,
  });

  const name = useInput<InputProps>({
    name: 'name',
    isDisabled: !isEditable,
    initialValue: data.name,
    label: formatMessage(docFormMessages.name),
    validate: validate,
    validator: (value) =>
      usableItems?.includes('name') && !value
        ? formatMessage(validatorMessages.buildingDescriptionError)
        : null,
    onChange: formItemChangeCallback,
    ...formItemServerErrors.name,
  });

  const dmvs_id_podani = useInput<InputProps>({
    name: 'dmvs_id_podani',
    isDisabled: true,
    initialValue: data.dmvs_id_podani,
    label: formatMessage(docFormMessages.dmvs_id_podani),
  });

  const formatDateTime = (value?: string | null) =>
    value ? `${intl.formatDate(value)} ${intl.formatTime(value)}` : null;

  const created = useInput<InputProps>({
    name: 'created',
    isDisabled: true,
    initialValue: formatDateTime(data.created),
    label: formatMessage(docFormMessages.created),
  });

  const closed = useInput<InputProps>({
    name: 'closed',
    isDisabled: true,
    initialValue: formatDateTime(data.closed),
    label: formatMessage(docFormMessages.closed),
  });

  const dmvs_source = useInput<InputProps>({
    name: 'dmvs_source',
    isDisabled: true,
    initialValue: data.dmvs_source,
    label: formatMessage(docFormMessages.dmvs_source),
  });

  const geodesist_official_number = useInput<InputProps>({
    name: 'geodesist_official_number',
    isDisabled: true,
    initialValue: data.geodesist?.official_number ? `${data.geodesist.official_number}` : null,
    label: formatMessage(docFormMessages.azi_cislo_opravneni),
  });

  const description = useInput<InputProps>({
    name: 'description',
    isDisabled: !isEditable,
    initialValue: data.description,
    label: formatMessage(docFormMessages.description),
    validate: validate,
    validator: (value) =>
      usableItems?.includes('description') && !value
        ? formatMessage(validatorMessages.objectDescriptionError)
        : null,
    onChange: formItemChangeCallback,
    ...formItemServerErrors.description,
  });

  const note = useInput<InputProps>({
    name: 'note',
    isDisabled: !isEditable,
    initialValue: data.note,
    label: formatMessage(docFormMessages.note),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.note,
  });

  const locationAutocompleteRef = useRef({});
  const handleLocationAutocompleteChange = (
    _: React.ChangeEvent,
    data: { ident: string; value: string }[],
  ) => {
    locationAutocompleteRef.current = data;
  };
  const location = useInput<InputProps>({
    name: 'location',
    isDisabled: !!data.id || !isEditable,
    initialValue: data.location,
    label: formatMessage(docFormMessages.location),
    validate: validate,
    onChange: formItemChangeCallback,
  });

  const planimetryChangeCallback = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      hasPlanimetryRef.current = e.target.checked;
      formItemChangeCallback();
    },
    [formItemChangeCallback],
  );
  const has_planimetry = useCheckbox<any>({
    name: 'has_planimetry',
    isDisabled: !isEditable,
    initialValue: !!data.has_planimetry,
    onChange: planimetryChangeCallback,
    ...formItemServerErrors.has_planimetry,
  });

  const utilitiesChangeCallback = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      hasUtilitiesRef.current = e.target.checked;
      formItemChangeCallback();
    },
    [formItemChangeCallback],
  );
  const has_utilities = useCheckbox<any>({
    name: 'has_utilities',
    isDisabled: !isEditable,
    initialValue: !!data.has_utilities,
    onChange: utilitiesChangeCallback,
    ...formItemServerErrors.has_utilities,
  });

  const prepareEditorManagerItems = useCallback(async (search: string) => {
    const response = await usersList({
      query: { search, roles: ['ADMIN'], is_editor: true, is_active: true },
    });
    if (response.ok) {
      return response.data?.results;
    }
    return null;
  }, []);

  const editor_manager = useSelect<DocsRetrieveResponse['editor_manager'], UserListResponse>({
    name: 'editor_manager',
    isDisabled: !isEditable,
    label: formatMessage(docFormMessages.editor_manager),
    initialValue: data.editor_manager,
    onChange: formItemChangeCallback,
    prepareItems: prepareEditorManagerItems,
    renderValue: (item) => (item?.name || item?.surname ? [item.name, item.surname].join(' ') : ''),
    renderItem: renderUserSelectItem,
    ...formItemServerErrors.editor_manager,
  });

  const prepareAuthorItems = useCallback(async (search: string) => {
    const response = await usersList({ query: { search } });
    if (response.ok) {
      return response.data?.results;
    }
    return null;
  }, []);

  const author = useSelect<DocsRetrieveResponse['author'], UserListResponse>({
    name: 'author',
    isDisabled: !isEditable,
    label: formatMessage(docFormMessages.author),
    initialValue: data.author,
    validate: validate,
    validator: (value) =>
      acl.docsUpdateAuthor &&
      usableItems?.includes('author') &&
      (!value || (!value.name && !value.surname))
        ? formatMessage(validatorMessages.authorError)
        : null,
    onChange: formItemChangeCallback,
    prepareItems: prepareAuthorItems,
    renderValue: (item) => (item.name || item.surname ? `${item.name} ${item.surname}` : ''),
    renderItem: renderUserSelectItem,
    ...formItemServerErrors.author,
  });

  const prepareGeodesistItems = useCallback(async (search: string) => {
    const response = await geodesistsList({ query: { search } });
    if (response.ok) {
      return response.data?.results ?? null;
    }
    return null;
  }, []);

  const geodesist = useSelect<DocsRetrieveResponse['geodesist'], DocsRetrieveResponse['geodesist']>(
    {
      name: 'geodesist',
      isDisabled: !isEditable,
      label: formatMessage(docFormMessages.geodesist),
      initialValue: data.geodesist,
      validate: validate,
      validator: (value) =>
        usableItems?.includes('geodesist') && (!value || (!value.name && !value.surname))
          ? formatMessage(validatorMessages.geodesistError)
          : null,
      onChange: formItemChangeCallback,
      prepareItems: prepareGeodesistItems,
      renderValue: (item) => (item.name || item.surname ? `${item.name} ${item.surname}` : ''),
      renderItem: renderUserSelectItem,
      ...formItemServerErrors.geodesist,
    },
  );

  // MULTISELECT
  const prepareCadastersItems = useCallback(async (val: string) => {
    const response = await cadastersList({ query: { name__icontains: val } });
    if (response.ok) {
      return response.data?.results;
    }
    return null;
  }, []);
  const cadasters = useMultiSelect({
    name: 'cadasters',
    isDisabled: !isEditable,
    label: formatMessage(docFormMessages.cadasters),
    initialValue: data.cadasters,
    validate: validate,
    validator: (value) =>
      usableItems?.includes('cadasters') && (!value || value.length === 0)
        ? formatMessage(validatorMessages.cadastersError)
        : null,
    onChange: formItemChangeCallback,
    prepareItems: prepareCadastersItems,
    renderValue: (item) => (item.name ? item.name : ''),
    renderItem: renderCadasterMultiSelectItem,
    ...formItemServerErrors.cadasters,
  });
  const cadastersDetail = useInput<InputProps>({
    name: 'cadasters',
    isDisabled: true,
    label: formatMessage(docFormMessages.cadasters),
    initialValue: data.cadasters.map((item) => item.name).join(', ') ?? '',
  });

  const state = { value: data.state };
  const formItems = {
    state,
    name,
    description,
    note,
    has_planimetry,
    has_utilities,
    author,
    geodesist,
    cadasters,
    ownerAutocompleteRef,
    location,
    locationAutocompleteRef,
    processorAutocompleteRef,
    utilities_type,
    editor_manager,
  } as const;

  const uploadHelpText = formatMessage.fallback(['document.check.uploadHelpText']);

  const handleCancelLogDialogBtnClick = useCallback(() => {
    setCheckLogId(undefined);
    showLogDialog(false);
  }, []);

  const processorKey = useMemo(() => {
    return `${data.processor_name}-${data.processor_ico}-${data.processor_street}-${data.processor_city}-${data.processor_postal_code}`;
  }, [
    data.processor_name,
    data.processor_ico,
    data.processor_street,
    data.processor_city,
    data.processor_postal_code,
  ]);
  const ownerKey = useMemo(() => {
    return `${data.owner_name}-${data.owner_ico}-${data.owner_street}-${data.owner_city}-${data.owner_postal_code}`;
  }, [data.owner_name, data.owner_ico, data.owner_street, data.owner_city, data.owner_postal_code]);

  return (
    <MainContent width='full' key={`${resource.data?.id}`}>
      {(resource.queryState === QUERY_STATE.SUCCESS || !docId) && (
        <article>
          <div
            className={classNames({
              'dmx-doc--head sg-u-vs-2': true,
              'dmx-is-dtm': data.original_id,
            })}
          >
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
                <PageTitle>
                  {formatMessage.lookup(docTitleMessages, 'doc', {
                    id: data.id || 'new',
                  })}
                </PageTitle>
                {data.state && (
                  <DocBadge
                    docState={data.state}
                    flow={data.flow}
                    rejectedBySubjekty={data.rejected_by_subjekty}
                  />
                )}
              </header>
              <BtnBar>
                {isEditable && (
                  <CtaBtn
                    queryState={toBtnQueryState(saveQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSaveDocWithValidation}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnSave} />
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionAccept && (
                  <CtaBtn
                    queryState={toBtnQueryState(signQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleAcceptBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnAccept} />
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionSubmit && (
                  <CtaBtn
                    queryState={toBtnQueryState(checkSubmitState)}
                    isDisabled={isActionRunning}
                    onClick={handleSubmitBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnSubmit} />
                  </CtaBtn>
                )}

                {!!docId && acl.canUploadSignedProtocol && (
                  <UploadBtn
                    isDisabled={uploadProtocolQueryState === QUERY_STATE.WAITING}
                    onChange={handleProtocolUpload}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnUploadSignedProtocol} />
                  </UploadBtn>
                )}

                {!!docId && acl.docsActionCloseInterrupt && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(closeInterruptQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCloseInterruptBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnInterrupt} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionImportInterrupt && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(importInterruptQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleImportInterruptBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnInterrupt} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionClose && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(closeQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCloseBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnClose} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionReject && (
                  <SecondaryBtn
                    isDanger
                    isDisabled={isActionRunning}
                    onClick={handleRejectBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnReject} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCancel && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(checkCancelState)}
                    isDisabled={isActionRunning}
                    onClick={handleCancelBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnCancel} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsDestroy && (
                  <SecondaryBtn
                    isDisabled={isActionRunning || removeQueryState === QUERY_STATE.WAITING}
                    isDanger
                    onClick={handleDelete}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnRemove} />
                  </SecondaryBtn>
                )}
              </BtnBar>
            </div>
            {showLogDownloadError && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.fileDownloadError} />
              </DangerAlert>
            )}
            {(resource.queryState === QUERY_STATE.WAITING ||
              saveQueryState === QUERY_STATE.WAITING ||
              deleteQueryState === QUERY_STATE.WAITING ||
              closeQueryState === QUERY_STATE.WAITING ||
              closeInterruptQueryState === QUERY_STATE.WAITING ||
              signQueryState === QUERY_STATE.WAITING ||
              checkCancelState === QUERY_STATE.WAITING ||
              checkSubmitState === QUERY_STATE.WAITING ||
              importInterruptQueryState === QUERY_STATE.WAITING) && (
              <WaitingAlert>
                <FormattedMessage {...docEditMessages.queryStateWaiting} />
              </WaitingAlert>
            )}
            {(saveQueryState === QUERY_STATE.FAIL || deleteQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateFail} />
              </DangerAlert>
            )}
            {signQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.signQueryStateFail} />
              </DangerAlert>
            )}
            {saveQueryState === QUERY_STATE.SUCCESS && (
              <SuccessAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateSuccess} />
              </SuccessAlert>
            )}
            {formItemServerErrors.non_field_errors && (
              <DangerAlert>{formItemServerErrors.non_field_errors.errorMessage!}</DangerAlert>
            )}
            {resource.queryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.loadQueryStateFail} />
              </DangerAlert>
            )}
            {([DOC_STATE.REJECTED] as DOC_STATE[]).includes(data.state) &&
              (resource.queryState === QUERY_STATE.SUCCESS || !docId) &&
              data.rejection_reason &&
              REJECTION_REASON_STATES.includes(data.state) && (
                <WarningAlert>
                  <b className='sg-u-type-bold'>
                    <FormattedMessage {...rejectionMessages.reasonTitle} />
                    {' - '}
                    <FormattedMessage
                      {...rejectionReasonMessages[data.rejection_reason ?? 'null']}
                    />
                  </b>
                  {data.rejection_note && (
                    <p className='dmx-doc--warningAlertText sg-a-mt-1/2'>{data.rejection_note}</p>
                  )}
                </WarningAlert>
              )}
            {closeQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.closeQueryStateFail} />
              </DangerAlert>
            )}
            {(closeInterruptQueryState === QUERY_STATE.FAIL ||
              importInterruptQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.interruptQueryStateFail} />
              </DangerAlert>
            )}
          </div>
          <div className='dmx-doc--cols'>
            <div className='sg-u-vs-4'>
              <Form onSubmit={handleSaveDocWithValidation}>
                <div className='sg-u-vs-4'>
                  <section>
                    <h3 className='dmx-doc--attributesTitle sg-u-type-h2 sg-a-ml-2'>&nbsp;</h3>
                    <div className='sg-u-box sg-a-p-2 sg-u-vs-2 sg-a-mt-1'>
                      {usableItems?.includes('name') && (
                        <FormRow>
                          <FormItem {...name} isRequired={true}>
                            <Input {...name} />
                          </FormItem>
                        </FormRow>
                      )}
                      {!!data.original_id && usableItems?.includes('dmvs_id_podani') && (
                        <FormRow>
                          <FormItem {...dmvs_id_podani}>
                            <Input {...dmvs_id_podani} />
                          </FormItem>
                        </FormRow>
                      )}
                      {!!data.original_id &&
                        (usableItems?.includes('created') || usableItems?.includes('closed')) && (
                          <FormRow>
                            {usableItems?.includes('created') && (
                              <FormItem {...created}>
                                <Input {...created} />
                              </FormItem>
                            )}
                            {usableItems?.includes('closed') && (
                              <FormItem {...closed}>
                                <Input {...closed} />
                              </FormItem>
                            )}
                          </FormRow>
                        )}
                      {!!docId && usableItems?.includes('dmvs_source') && (
                        <FormRow>
                          <FormItem {...dmvs_source}>
                            <Input {...dmvs_source} />
                          </FormItem>
                        </FormRow>
                      )}

                      {usableItems?.includes('geodesist_official_number') &&
                        data.prejimka_type === PrejimkaTypesEnum.DMVS_GAD_CHECK && (
                          <FormRow>
                            <FormItem {...geodesist_official_number}>
                              <Input {...geodesist_official_number} />
                            </FormItem>
                          </FormRow>
                        )}
                      {usableItems?.includes('description') && (
                        <FormRow>
                          <FormItem {...description} isRequired>
                            <Textarea {...description} />
                          </FormItem>
                        </FormRow>
                      )}

                      {(usableItems?.includes('buildingCategory') ||
                        usableItems?.includes('utilities')) && (
                        <FormRow>
                          {usableItems?.includes('buildingCategory') && (
                            <FormItem label={formatMessage(docFormMessages.buildingCategory)}>
                              <div className='sg-a-d-f sg-a-fw-w sg-a-cg-2 sg-a-rg-1'>
                                <Checkbox {...has_planimetry} isChecked={has_planimetry.isChecked}>
                                  <FormattedMessage {...docFormMessages.has_planimetry} />
                                </Checkbox>
                                <Checkbox {...has_utilities}>
                                  <FormattedMessage {...docFormMessages.has_utilities} />
                                </Checkbox>
                              </div>
                            </FormItem>
                          )}
                          {usableItems?.includes('utilities') && (
                            <FormItem {...utilities_type}>
                              <Select {...utilities_type} />
                            </FormItem>
                          )}
                        </FormRow>
                      )}
                      {usableItems?.includes('cadasters') && (
                        <FormRow>
                          {data.id ? (
                            <FormItem {...cadastersDetail}>
                              <Input {...cadastersDetail} />
                            </FormItem>
                          ) : (
                            <FormItem {...cadasters} isRequired>
                              <MultiSelect {...cadasters} />
                            </FormItem>
                          )}
                        </FormRow>
                      )}
                      {usableItems?.includes('location') && (
                        <FormRow>
                          {data.id ? (
                            <FormItem {...location}>
                              <Input {...location} />
                            </FormItem>
                          ) : (
                            <LocationWhisperer
                              label={formatMessage(docFormMessages.location)}
                              serverError={formItemServerErrors.location}
                              errorMessage={formatMessage(validatorMessages.locationError)}
                              value={data.location ?? ''}
                              validate={validate}
                              formItemChangeCallback={formItemChangeCallback}
                              onChange={handleLocationAutocompleteChange}
                            />
                          )}
                        </FormRow>
                      )}
                      {usableItems?.includes('geodesist') && (
                        <FormRow>
                          <FormItem {...geodesist} isRequired>
                            <CustomSelect {...geodesist} />
                          </FormItem>
                        </FormRow>
                      )}
                      {usableItems?.includes('note') && (
                        <FormRow>
                          <FormItem {...note}>
                            <Textarea {...note} />
                          </FormItem>
                        </FormRow>
                      )}
                      {acl.docsUpdateAuthor && usableItems?.includes('author') && (
                        <FormRow>
                          <FormItem {...author} isRequired>
                            <CustomSelect {...author} />
                          </FormItem>
                        </FormRow>
                      )}
                      {usableItems?.includes('editor_manager') &&
                        (acl.docsUpdateEditorManager ||
                          EDITOR_VISIBLE_STATE.includes(data.state)) && (
                          <FormRow>
                            <FormItem {...editor_manager}>
                              <CustomSelect {...editor_manager} />
                            </FormItem>
                          </FormRow>
                        )}
                    </div>
                  </section>

                  {usableItems?.includes('owner') && (
                    <section>
                      <h3 className='sg-u-type-h2 sg-a-ml-2'>
                        <FormattedMessage {...docFormMessages.owner} />
                      </h3>
                      <div className='sg-u-box sg-a-p-2 sg-u-vs-2 sg-a-mt-1'>
                        <ResWhisperer
                          key={ownerKey}
                          isDisabled={!isEditable}
                          serverErrors={formItemServerErrors}
                          nameIdent='owner_name'
                          nameValue={data.owner_name ?? ''}
                          nameErrorMessage={formatMessage(validatorMessages.ownerNameError)}
                          icoIdent='owner_ico'
                          icoValue={data.owner_ico ?? ''}
                          icoErrorMessage={formatMessage(validatorMessages.ownerIcoError)}
                          birthNumberIdent=''
                          birthNumberValue={''}
                          streetIdent='owner_street'
                          streetValue={data.owner_street ?? ''}
                          cityIdent='owner_city'
                          cityValue={data.owner_city ?? ''}
                          postalCodeIdent='owner_postal_code'
                          postalCodeValue={data.owner_postal_code ?? ''}
                          validate={false}
                          formItemChangeCallback={formItemChangeCallback}
                          onChange={handleOwnerAutocompleteChange}
                        />
                      </div>
                    </section>
                  )}

                  {usableItems?.includes('processor') && (
                    <section>
                      <h3 className='sg-u-type-h2 sg-a-ml-2'>
                        <FormattedMessage {...docFormMessages.processor} />
                      </h3>
                      <div className='sg-u-box sg-a-p-2 sg-u-vs-2 sg-a-mt-1'>
                        <ResWhisperer
                          key={processorKey}
                          isDisabled={!isEditable}
                          serverErrors={formItemServerErrors}
                          nameIdent='processor_name'
                          nameValue={data.processor_name ?? ''}
                          nameErrorMessage={formatMessage(validatorMessages.processorNameError)}
                          icoIdent='processor_ico'
                          icoValue={data.processor_ico ?? ''}
                          icoErrorMessage={formatMessage(validatorMessages.processorIcoError)}
                          birthNumberIdent=''
                          birthNumberValue={''}
                          streetIdent='processor_street'
                          streetValue={data.processor_street ?? ''}
                          cityIdent='processor_city'
                          cityValue={data.processor_city ?? ''}
                          postalCodeIdent='processor_postal_code'
                          postalCodeValue={data.processor_postal_code ?? ''}
                          validate={false}
                          formItemChangeCallback={formItemChangeCallback}
                          onChange={handleProcessorAutocompleteChange}
                        />
                      </div>
                    </section>
                  )}
                </div>
              </Form>

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...docFormMessages.attachments} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-u-vs-3'>
                  {usableItems?.includes('attachments') && !!docId && (
                    <section>
                      <h3 className='sg-u-type-h2 sg-a-ml-2'>
                        <FormattedMessage {...docFormMessages.attachments_docs} />
                      </h3>
                      <div className='sg-u-box sg-a-d-ib sg-a-mw-100% sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                        {uploadHelpText && (
                          <div className='sg-a-mw-fc'>
                            <InfoAlert>{uploadHelpText}</InfoAlert>
                          </div>
                        )}
                        <DocAttachments
                          isDisabled={!isEditable}
                          doc={resource?.data}
                          prejimkaType={data.prejimka_type}
                          createDoc={handleSaveDocWithoutValidation}
                          attachments={stateData.attachments}
                        />
                      </div>
                    </section>
                  )}
                  <section className='sg-a-mt-2'>
                    <DocDownloadFiles
                      title={<FormattedMessage {...docFormMessages.attachments_system} />}
                      docId={docId}
                      items={[
                        {
                          permission: acl.docsActionDownloadProtocol,
                          link: handoverProtocolDownloadLink,
                          label:
                            data.state === DOC_STATE.SIGNING ? (
                              <FormattedMessage
                                {...docEditButtonsMsg.docBtnDownloadProtocolToSign}
                              />
                            ) : (
                              <FormattedMessage {...docEditButtonsMsg.docBtnDownloadProtocol} />
                            ),
                          ext: 'zip',
                        },
                        {
                          permission: acl.docsActionDownloadCloseLog,
                          link: closeLogDownloadLink,
                          label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadLog} />,
                          ext: 'zip',
                        },
                        {
                          permission: acl.docsActionDownloadImportLog,
                          link: importLogDownloadLink,
                          label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadLog} />,
                          ext: 'zip',
                        },
                      ]}
                    />
                  </section>
                </div>
              </section>
            </div>

            <div className='dmx-doc--rightCol sg-u-vs-4'>
              {usableItems?.includes('map') &&
                mapState &&
                resource.data?.rozsah_aktualizacni_zmeny_extent &&
                resource.data.rozsah_aktualizacni_zmeny_extent.length > 0 && (
                  <section className='sg-a-f-a sg-a-d-f sg-a-fd-c sg-a-rg-1'>
                    <h3 className='sg-u-type-h2 sg-a-ml-2'>
                      <FormattedMessage {...docFormMessages.docAreaTitle} />
                    </h3>
                    <div className='sg-u-box sg-a-p-2 sg-a-f-a'>
                      <MapWrapper>
                        <MapWithAreas state={mapState} extent={mapExtent} />
                      </MapWrapper>
                    </div>
                  </section>
                )}

              {resource?.data?.checks && resource?.data?.checks.length > 0 && showChecks && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2'>
                    <FormattedMessage {...docFormMessages.checks} />
                  </h3>
                  <div className='sg-u-box sg-a-mt-1 dmx-a-mw-fc'>
                    <CheckRecordList
                      docId={docId}
                      items={resource?.data?.checks}
                      onClick={handleCheckClick}
                      onRefetch={handleRefetch}
                    />
                  </div>
                </section>
              )}

              {showLogs && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...docFormMessages.logs} />
                  </h3>
                  <LogList objectId={docId!} resourceType={logResourceType} />
                </section>
              )}
              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                  <FormattedMessage {...docFormMessages.history} />
                </h3>
                <HistoryList history={data.history} />
              </section>
            </div>
          </div>

          {isLogDialogShown && (
            <Dialog
              onEscKeyDown={handleCancelLogDialogBtnClick}
              onOverlayClick={handleCancelLogDialogBtnClick}
            >
              <CloseDialogBtn onClick={handleCancelLogDialogBtnClick} />
              <div className='sg-a-p-4 sg-u-vs-2'>
                <PageTitle>
                  <FormattedMessage {...docFormMessages.logs} />
                </PageTitle>
                <LogList objectId={checkLogId!} resourceType={logResourceType} />
              </div>
            </Dialog>
          )}

          {isRejectionDialogShown && !!docId && (
            <Dialog width='500px'>
              <Rejection
                docId={docId}
                onCancelBtnClick={handleRejectCancelBtnClick}
                resource={resource}
                partialUpdateCallback={brnoDocsRejectPartialUpdate}
              />
            </Dialog>
          )}
        </article>
      )}
    </MainContent>
  );
};
